import {
  ScmIntegrationsApi,
  scmIntegrationsApiRef,
  ScmAuth,
} from '@backstage/integration-react';
import {
  AnyApiFactory,
  ApiRef,
  BackstageIdentityApi,
  configApiRef,
  createApiFactory,
  createApiRef,
  discoveryApiRef,
  errorApiRef,
  alertApiRef,
  identityApiRef,
  OAuthApi,
  oauthRequestApiRef,
  ProfileInfoApi,
  SessionApi,
  analyticsApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import { toastApiRef } from '@backstage/frontend-plugin-api';
import { OAuth2 } from '@backstage/core-app-api';
import { visitsApiRef, VisitsWebStorageApi } from '@backstage/plugin-home';
import { DocAnalyticsApi } from '@internal/plugin-doc-analytics';
import { oneadvancedApiRef, OneAdvancedClient } from '@internal/plugin-oneadvanced';
import { techRadarApiRef } from '@backstage-community/plugin-tech-radar';
import { TechRadarClient } from './lib/TechRadarClient';

export const oidcAuthApiRef: ApiRef<
  OAuthApi & ProfileInfoApi & BackstageIdentityApi & SessionApi
> = createApiRef({
  id: 'auth.oidc',
});

export const apis: AnyApiFactory[] = [
  createApiFactory({
    api: analyticsApiRef,
    deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
    factory: ({ discoveryApi, fetchApi }) => new DocAnalyticsApi(discoveryApi, fetchApi),
  }),
  createApiFactory({
    api: oneadvancedApiRef,
    deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
    factory: ({ discoveryApi, fetchApi }) => new OneAdvancedClient({ discoveryApi, fetchApi }),
  }),
  createApiFactory({
    api: visitsApiRef,
    deps: { errorApi: errorApiRef, identityApi: identityApiRef },
    factory: ({ errorApi, identityApi }) =>
      VisitsWebStorageApi.create({ errorApi, identityApi }),
  }),
  createApiFactory({
    api: scmIntegrationsApiRef,
    deps: { configApi: configApiRef },
    factory: ({ configApi }) => ScmIntegrationsApi.fromConfig(configApi),
  }),
  ScmAuth.createDefaultApiFactory(),
  createApiFactory({
    api: toastApiRef,
    deps: { alertApi: alertApiRef },
    factory: ({ alertApi }) => ({
      post(toast) {
        const statusMap = {
          danger: 'error',
          warning: 'warning',
          info: 'info',
          success: 'success',
          neutral: 'success',
        } as const;
        const severity = toast.status !== undefined ? (statusMap[toast.status] ?? 'success') : 'success' as const;
        alertApi.post({
          message: toast.title as string,
          severity,
          display: 'transient',
        });
        return { close: () => {} };
      },
    }),
  }),
  createApiFactory({
    api: techRadarApiRef,
    deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
    factory: ({ discoveryApi, fetchApi }) => new TechRadarClient(discoveryApi, fetchApi),
  }),
  createApiFactory({
    api: oidcAuthApiRef,
    deps: {
      discoveryApi: discoveryApiRef,
      oauthRequestApi: oauthRequestApiRef,
      configApi: configApiRef,
    },
    factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
      OAuth2.create({
        discoveryApi,
        oauthRequestApi,
        configApi,
        provider: {
          id: 'oidc',
          title: 'OneAdvanced SSO',
          icon: () => null,
        },
        defaultScopes: ['openid', 'profile', 'email'],
        popupOptions: { size: { width: 1000, height: 800 } },
      }),
  }),
];
